import { useState } from "react";
import { Box, Flex, Portal, Text } from "@chakra-ui/react";
import LocalSpinner from "../../../../components/LocalSpinner";
import { getFormattedNumValue } from "../../../../utils";
import DashboardCard from "../../../ISSB/Dashboard/components/DashboardCard";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../config";
import BigNumber from "../components/BigNumber";
import { getYoYChange } from "../../../../utils/yoy";

const DrillHint = ({ label, children }) => {
  const [pos, setPos] = useState(null);
  return (
    <Box
      height="100%"
      onMouseMove={(e) => setPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos && (
        <Portal>
          <Box
            position="fixed"
            left={`${pos.x}px`}
            top={`${pos.y - 14}px`}
            transform="translate(-50%, -100%)"
            pointerEvents="none"
            zIndex="tooltip"
            bg="white"
            color="#4A5568"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="4px"
            boxShadow="0 1px 2px rgba(0,0,0,0.05)"
            fontSize="13px"
            px={3}
            py={2}
            whiteSpace="nowrap"
          >
            {label}
          </Box>
        </Portal>
      )}
    </Box>
  );
};

const AttributedEmissions = ({ isLoading, currentYear, previousYear, theme = "light" }) => {
  const navigate = useNavigate();

  const colors = {
    light: { cardBg: "#F7FAFC", title: "teal", value: "gray.800" },
    dark: { cardBg: "pureBlack", title: "pureWhite", value: "lightLime" },
  };
  const t = colors[theme] || colors.light;

  // YoY change of total attributed emissions (a decrease is an improvement —
  // matches YoYChip's default down=green semantics, so pass straight through).
  const yoy = getYoYChange(currentYear?.totalEmissions, previousYear?.totalEmissions);

  // Delta of actuals vs target (NOT a year-on-year change) — shown as a plain
  // labelled figure, not a YoYChip, so it isn't read as an improvement/decline.
  const delta = currentYear?.totalTarget
    ? ((currentYear?.totalEmissions - currentYear?.totalTarget) / currentYear?.totalTarget) * 100
    : 0;

  return (
    <DrillHint label="Click to drill down into attributed emissions">
    <DashboardCard bg={t.cardBg} px={6} onClick={() => navigate(routes.supplierInsightEmissionDetail)}>
      <Text fontSize="body.2xl" fontWeight="bold" color={t.title}>
        Attributed Emissions
      </Text>
      {isLoading ? (
        <LocalSpinner size="sm" />
      ) : (
        <Flex direction="column" gap={8} height="100%" justify="space-between">
          <Box mb={4}>
            <BigNumber color={t.value} pctChange={yoy}>
              {getFormattedNumValue(currentYear?.totalEmissions, 2, true) || 0}
            </BigNumber>
            <Text fontSize="body.s" fontWeight="semibold" color="gray.500" mt={1}>
              tCO2e
            </Text>
          </Box>

          <Box>
            <Text fontSize="body.s" fontWeight="semibold" color="gray.500">
              Spend Intensity
            </Text>
            <BigNumber color={t.value}>
              {getFormattedNumValue(currentYear?.spendIntensity, 2, true) || 0}
            </BigNumber>
            <Text fontSize="body.xs" color="gray.500" mt={1}>
              tCO2e/£1m spend
            </Text>
          </Box>

          <Box>
            <Text fontSize="body.s" fontWeight="semibold" color="gray.500">
              vs target
            </Text>
            <Text fontSize="body.l" fontWeight="700" color={t.value}>
              {getFormattedNumValue(delta, 2) || 0} %
            </Text>
          </Box>
        </Flex>
      )}
    </DashboardCard>
    </DrillHint>
  );
};

export default AttributedEmissions;
